import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, showError } from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import { buildBillingAggregateCostV822 } from './billingCostStateV822';

const formatPeriod = (start, end) => {
  if (!start || !end) return '-';
  return `${new Date(start * 1000).toLocaleDateString()} 閳?${new Date(
    end * 1000
  ).toLocaleDateString()}`;
};

const confidenceLabel = (confidence, t) => {
  const entries = Object.entries(confidence || {}).filter(
    ([, count]) => Number(count) > 0
  );
  if (!entries.length) return '-';
  return entries
    .map(
      ([source, count]) =>
        `${t(`billing.profitability.confidence.${source}`)} ${count}`
    )
    .join(' 璺?');
};

const ProfitabilityMetric = ({ label, value, hint, warning }) => (
  <div className={'billing-profit-metric-v82' + (warning ? ' warning' : '')}>
    <span>{label}</span>
    <strong>{value}</strong>
    {hint ? <small>{hint}</small> : null}
  </div>
);

const BillingProfitabilityV82 = ({ onSelectAccount = () => {} }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readPageSizeV8('v82-page-size-profitability', 20)
  );
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [usageSource, setUsageSource] = useState('');
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  const [sort, setSort] = useState('list_amount_desc');

  const usageOptions = useMemo(
    () => [
      {
        key: 'all',
        value: '',
        text: t('billing.profitability.filters.all_confidence'),
      },
      ...[
        'provider_detailed',
        'provider_aggregate',
        'estimated',
        'fallback_rule',
        'missing',
      ].map((source) => ({
        key: source,
        value: source,
        text: t(`billing.profitability.confidence.${source}`),
      })),
    ],
    [t]
  );
  const sortOptions = useMemo(
    () =>
      [
        'list_amount_desc',
        'gross_profit_desc',
        'request_count_desc',
        'account_id_desc',
      ].map((value) => ({
        key: value,
        value,
        text: t(`billing.profitability.sort.${value}`),
      })),
    [t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        sort,
      });
      if (search) params.set('search', search);
      if (usageSource) params.set('usage_source', usageSource);
      if (anomaliesOnly) params.set('anomalies_only', 'true');
      const response = await API.get(
        `/api/billing/v8/admin/profitability?${params.toString()}`
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setRows(response.data.data?.rows || []);
      setTotals(response.data.data?.totals || null);
      setTotal(response.data.data?.total || 0);
    } catch (error) {
      showError(error.message || t('billing.profitability.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [anomaliesOnly, page, pageSize, search, sort, t, usageSource]);

  useEffect(() => {
    load().then();
  }, [load]);

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const totalCost = totals
    ? buildBillingAggregateCostV822(totals, 'profitability', t)
    : null;

  return (
    <section className='billing-profitability-v82'>
      <div className='billing-profitability-heading-v82'>
        <div>
          <Header as='h3'>{t('billing.profitability.title')}</Header>
          <span className='billing-muted-v8'>
            {t('billing.profitability.subtitle')}
          </span>
        </div>
        <Button
          type='button'
          basic
          icon
          labelPosition='left'
          loading={loading}
          onClick={load}
        >
          <Icon name='refresh' />
          {t('billing.profitability.actions.refresh')}
        </Button>
      </div>

      <div className='billing-profitability-filters-v82'>
        <Form.Input
          action={{
            icon: 'search',
            content: t('billing.profitability.actions.search'),
            onClick: applySearch,
          }}
          value={searchInput}
          placeholder={t('billing.profitability.filters.search')}
          onChange={(_, { value }) => setSearchInput(value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applySearch();
            }
          }}
        />
        <Dropdown
          selection
          options={usageOptions}
          value={usageSource}
          onChange={(_, { value }) => {
            setPage(1);
            setUsageSource(value);
          }}
        />
        <Dropdown
          selection
          options={sortOptions}
          value={sort}
          onChange={(_, { value }) => {
            setPage(1);
            setSort(value);
          }}
        />
        <Checkbox
          toggle
          label={t('billing.profitability.filters.anomalies_only')}
          checked={anomaliesOnly}
          onChange={(_, { checked }) => {
            setPage(1);
            setAnomaliesOnly(checked);
          }}
        />
      </div>

      {totals ? (
        <div className='billing-profitability-metrics-v82'>
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.list')}
            value={`USD ${totals.list_amount_usd}`}
            hint={`CNY ${totals.list_amount_cny}`}
          />
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.discount')}
            value={`- USD ${totals.discount_amount_usd}`}
          />
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.customer_net')}
            value={`USD ${totals.customer_net_amount_usd}`}
          />
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.baseline_cost')}
            value={totalCost.baselineValue}
          />
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.provider_cost')}
            value={totalCost.value}
            warning={!totalCost.complete}
            hint={[
              totalCost.hint,
              t('billing.profitability.additional_attempt_cost_hint', {
                amount: totals.additional_attempt_cost_usd,
              }),
            ]
              .filter(Boolean)
              .join(' ')}
          />
          <ProfitabilityMetric
            label={t('billing.profitability.metrics.gross_profit')}
            value={totalCost.profitValue}
            hint={totalCost.profitHint}
            warning={!totalCost.complete}
          />
          <ProfitabilityMetric
            warning={
              Number(totals.unknown_cost_request_count) > 0 ||
              Number(totals.unknown_attempt_cost_count) > 0
            }
            label={t('billing.cost_v822.exception_records', {
              defaultValue: 'Cost / usage exception records',
            })}
            value={String(totals.anomaly_request_count)}
            hint={totalCost.hint}
          />
        </div>
      ) : null}

      <Message info>
        <Message.Header>
          {t('billing.profitability.known_cost_title')}
        </Message.Header>
        <p>{t('billing.profitability.known_cost_description')}</p>
      </Message>

      {loading && !rows.length ? (
        <Loader active inline='centered' />
      ) : rows.length ? (
        <>
          <div
            className='management-table-scroll-v72'
            role='region'
            tabIndex={0}
          >
            <Table
              basic='very'
              compact
              unstackable
              className='management-table-v72'
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.organization')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.requests')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.list')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.customer_net')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.cost')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.profit')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.profitability.table.confidence')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('billing.cost_v822.exception_records', {
                      defaultValue: 'Cost / usage exception records',
                    })}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => {
                  const cost = buildBillingAggregateCostV822(
                    row,
                    'profitability',
                    t
                  );
                  return (
                    <Table.Row key={row.account_id}>
                      <Table.Cell>
                        <Button
                          type='button'
                          basic
                          compact
                          onClick={() => onSelectAccount?.(row.account_id)}
                        >
                          {row.organization_name}
                        </Button>
                        <div className='billing-cell-secondary-v8'>
                          {row.owner_username || '-'} 璺瘂' '}
                          {formatPeriod(row.period_start, row.period_end)}
                        </div>
                      </Table.Cell>
                      <Table.Cell>{row.request_count}</Table.Cell>
                      <Table.Cell>
                        USD {row.list_amount_usd}
                        <span className='billing-cell-secondary-v8'>
                          CNY {row.list_amount_cny}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <strong>USD {row.customer_net_amount_usd}</strong>
                        <span className='billing-cell-secondary-v8'>
                          - USD {row.discount_amount_usd}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {cost.value}
                        {cost.hint ? (
                          <span className='billing-cell-secondary-v8'>
                            {cost.hint}
                          </span>
                        ) : null}
                        <span className='billing-cell-secondary-v8'>
                          {t('billing.profitability.table.baseline_short')}{' '}
                          {cost.baselineValue}
                        </span>
                        {Number(row.additional_attempt_cost_usd) > 0 ? (
                          <span className='billing-cell-secondary-v8'>
                            {t(
                              'billing.profitability.additional_attempt_cost',
                              {
                                amount: row.additional_attempt_cost_usd,
                              }
                            )}
                          </span>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        <strong>{cost.profitValue}</strong>
                        <span className='billing-cell-secondary-v8'>
                          {cost.profitHint}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {confidenceLabel(row.usage_confidence, t)}
                      </Table.Cell>
                      <Table.Cell>
                        {Number(row.anomaly_request_count) > 0 ? (
                          <Label color='orange' basic>
                            {row.anomaly_request_count}
                          </Label>
                        ) : (
                          <Label color='green' basic>
                            0
                          </Label>
                        )}
                        {Number(row.unknown_cost_request_count) > 0 ? (
                          <span className='billing-cell-secondary-v8'>
                            {t('billing.profitability.unknown_cost_short', {
                              count: row.unknown_cost_request_count,
                            })}
                          </span>
                        ) : null}
                        {Number(row.unknown_attempt_cost_count) > 0 ? (
                          <span className='billing-cell-secondary-v8'>
                            {t(
                              'billing.profitability.unknown_attempt_cost_short',
                              {
                                count: row.unknown_attempt_cost_count,
                              }
                            )}
                          </span>
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
          <div className='list-pagination-v8'>
            <PageSizeSelectV8
              label={t('log.table.page_size')}
              storageKey='v82-page-size-profitability'
              value={pageSize}
              onChange={(nextPageSize) => {
                setPage(1);
                setPageSize(nextPageSize);
              }}
            />
            {total > pageSize ? (
              <Pagination
                activePage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={(_, { activePage }) =>
                  setPage(Number(activePage))
                }
              />
            ) : null}
          </div>
        </>
      ) : (
        <Message content={t('billing.profitability.empty')} />
      )}
    </section>
  );
};

export default BillingProfitabilityV82;
